import { useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function resolveErrorMsg(status: number, body: Record<string, unknown>): string {
  const err = (body['error'] as string) ?? '';
  if (status === 429) return 'Too many requests — please wait a minute and try again.';
  if (status === 404 && /expired/i.test(err)) return 'This activation code has expired. Contact support for a new one.';
  if (status === 404) return 'Activation code not found. Check for typos and try again.';
  if (status === 409) return 'This activation code has already been redeemed by a different account.';
  if (status === 400) return `Validation error: ${err}`;
  return `An unexpected error occurred (${status}). Please try again or contact support.`;
}

export const RedeemPage = () => {
  const [code, setCode] = useState('');
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
      const res = await fetch(`${apiBase}/tenants/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim(),
          tenantName: orgName.trim(),
          adminEmail: email.trim(),
        }),
      });

      let body: Record<string, unknown> = {};
      try { body = await res.json(); } catch { /* ignore JSON parse errors */ }

      if (res.ok) {
        setSuccess(true);
      } else {
        console.debug('[RedeemPage] provision error', res.status, body);
        setErrorMsg(resolveErrorMsg(res.status, body));
      }
    } catch (err) {
      console.debug('[RedeemPage] network error', err);
      setErrorMsg('Network error — check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#F4F6F8',
        }}
      >
        <Paper sx={{ p: 4, width: '100%', maxWidth: 440 }} elevation={2}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Account Created!
            </Typography>
          </Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            A temporary password has been sent to <strong>{email}</strong>. Sign in and you will be prompted to set a permanent password.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Check your email inbox for the temporary password. If you do not see it within a few minutes, check your spam folder.
          </Typography>
          <Button component="a" href="#/login" variant="contained" fullWidth>
            Go to Sign In
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F4F6F8',
      }}
    >
      <Paper sx={{ p: 4, width: '100%', maxWidth: 440 }} elevation={2}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <VpnKeyIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" fontWeight={700} color="primary" letterSpacing="-0.02em">
            CHAMBER
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Activate your account
          </Typography>
        </Box>

        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Redeem Activation Code
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {errorMsg}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            id="redeem-code"
            label="Activation Code"
            fullWidth
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ autoCapitalize: 'characters', style: { textTransform: 'uppercase' } }}
          />
          <TextField
            id="redeem-org-name"
            label="Organization Name"
            fullWidth
            required
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            id="redeem-email"
            label="Admin Email"
            type="email"
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !code.trim() || !orgName.trim() || !email.trim()}
            sx={{ mb: 1.5 }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" aria-label="Activating account" />
            ) : (
              'Activate Account'
            )}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Link href="#/login" variant="body2" underline="hover">
            Already have an account? Sign in
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};
